export interface KeyboardNavigationKeys {
  left?: string[];
  right?: string[];
  up?: string[];
  down?: string[];
  home?: string[];
  end?: string[];
  pageUp?: string[];
  pageDown?: string[];
}

export interface KeyboardNavigationOptions {
  itemCount: number;
  initialIndex?: number;
  direction?: "vertical" | "horizontal";
  onIndexChange?: (index: number) => void;
  enabled?: boolean;
  targetElement?: HTMLElement | Window | null;
  keys?: KeyboardNavigationKeys;
}

export type KeyboardNavigationChangeCallback = (currentIndex: number) => void;

const HORIZONTAL_KEYS: Required<KeyboardNavigationKeys> = {
  left: ["ArrowLeft", "37"],
  right: ["ArrowRight", "39"],
  up: [],
  down: [],
  home: ["Home"],
  end: ["End"],
  pageUp: ["PageUp", "427"],
  pageDown: ["PageDown", "428"],
};

const VERTICAL_KEYS: Required<KeyboardNavigationKeys> = {
  left: [],
  right: [],
  up: ["ArrowUp", "38"],
  down: ["ArrowDown", "40"],
  home: ["Home"],
  end: ["End"],
  pageUp: ["PageUp", "427"],
  pageDown: ["PageDown", "428"],
};

class GlobalKeyboardHandler {
  private static instance: GlobalKeyboardHandler | null = null;
  private keyDownCallbacks: Set<(event: KeyboardEvent) => void> = new Set();
  private keyUpCallbacks: Set<(event: KeyboardEvent) => void> = new Set();
  private isListening: boolean = false;

  private constructor() {}

  public static getInstance(): GlobalKeyboardHandler {
    if (!GlobalKeyboardHandler.instance) {
      GlobalKeyboardHandler.instance = new GlobalKeyboardHandler();
    }
    return GlobalKeyboardHandler.instance;
  }

  private startListening() {
    if (!this.isListening) {
      window.addEventListener("keydown", this.handleKeyDown);
      window.addEventListener("keyup", this.handleKeyUp);
      this.isListening = true;
    }
  }

  private stopListening() {
    if (
      this.isListening &&
      this.keyDownCallbacks.size === 0 &&
      this.keyUpCallbacks.size === 0
    ) {
      window.removeEventListener("keydown", this.handleKeyDown);
      window.removeEventListener("keyup", this.handleKeyUp);
      this.isListening = false;
    }
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    this.keyDownCallbacks.forEach((cb) => cb(event));
  };

  private handleKeyUp = (event: KeyboardEvent) => {
    this.keyUpCallbacks.forEach((cb) => cb(event));
  };

  public registerKeyDown(callback: (event: KeyboardEvent) => void) {
    this.keyDownCallbacks.add(callback);
    this.startListening();
  }

  public unregisterKeyDown(callback: (event: KeyboardEvent) => void) {
    this.keyDownCallbacks.delete(callback);
    this.stopListening();
  }

  public registerKeyUp(callback: (event: KeyboardEvent) => void) {
    this.keyUpCallbacks.add(callback);
    this.startListening();
  }

  public unregisterKeyUp(callback: (event: KeyboardEvent) => void) {
    this.keyUpCallbacks.delete(callback);
    this.stopListening();
  }
}

export class KeyboardNavigationManager {
  private itemCount: number;
  private direction: "vertical" | "horizontal";
  private onIndexChange?: (index: number) => void;
  private enabled: boolean;
  private targetElement: HTMLElement | Window | null;
  private keys: Required<KeyboardNavigationKeys>;

  private currentIndex: number;

  private changeCallbacks: Set<KeyboardNavigationChangeCallback> = new Set();

  private handleKeyDown: ((event: KeyboardEvent) => void) | null = null;
  private handleKeyUp: ((event: KeyboardEvent) => void) | null = null;

  constructor(options: KeyboardNavigationOptions) {
    this.itemCount = options.itemCount;
    this.direction = options.direction ?? "horizontal";
    this.onIndexChange = options.onIndexChange;
    this.enabled = options.enabled ?? true;
    // Default to window if targetElement is not provided or is null
    this.targetElement = options.targetElement ?? window;
    this.keys =
      this.direction === "horizontal"
        ? { ...HORIZONTAL_KEYS, ...options.keys }
        : { ...VERTICAL_KEYS, ...options.keys };

    this.currentIndex = options.initialIndex ?? 0;

    if (this.targetElement === window) {
      const globalHandler = GlobalKeyboardHandler.getInstance();
      this.handleKeyDown = this.handleKeyDownEvent.bind(this);
      this.handleKeyUp = this.handleKeyUpEvent.bind(this);
      globalHandler.registerKeyDown(this.handleKeyDown);
      globalHandler.registerKeyUp(this.handleKeyUp);
    } else {
      this.setupEventListeners(); // Keep existing for non-window targets
    }
  }

  /**
   * Setup keyboard event listeners
   */
  private setupEventListeners(): void {
    this.handleKeyDown = (event: KeyboardEvent) => {
      this.handleKeyDownEvent(event);
    };

    this.handleKeyUp = (event: KeyboardEvent) => {
      this.handleKeyUpEvent(event);
    };

    this.attachListeners();
  }

  /**
   * Attach event listeners to the target element
   */
  private attachListeners(): void {
    if (!this.enabled || this.itemCount === 0) return;

    // Default to window if targetElement is null
    const target = this.targetElement ?? window;
    const isWindowTarget = target === window;
    const element = isWindowTarget ? window : (target as HTMLElement);

    if (this.handleKeyDown && this.handleKeyUp) {
      element.addEventListener("keydown", this.handleKeyDown as EventListener);
      element.addEventListener("keyup", this.handleKeyUp as EventListener);
    }
  }

  /**
   * Detach event listeners from the target element
   */
  private detachListeners(): void {
    // Default to window if targetElement is null
    const target = this.targetElement ?? window;
    const isWindowTarget = target === window;
    const element = isWindowTarget ? window : (target as HTMLElement);

    if (this.handleKeyDown && this.handleKeyUp) {
      element.removeEventListener(
        "keydown",
        this.handleKeyDown as EventListener
      );
      element.removeEventListener("keyup", this.handleKeyUp as EventListener);
    }
  }

  /**
   * Check if a key matches any of the configured keys
   */
  private matchesKey(event: KeyboardEvent, keyList: string[]): boolean {
    return (
      keyList.includes(event.key) ||
      keyList.includes(event.keyCode.toString()) ||
      keyList.includes(event.code)
    );
  }

  /**
   * Handle keydown event
   */
  private handleKeyDownEvent(event: KeyboardEvent): void {
    const isWindowTarget = this.targetElement === window;
    const currentContainer =
      this.targetElement && this.targetElement !== window
        ? (this.targetElement as HTMLElement)
        : null;

    // Only handle if focused (or if using window)
    if (!isWindowTarget && currentContainer !== document.activeElement) {
      return;
    }

    const isHorizontal = this.direction === "horizontal";
    let shouldNavigate = false;
    let navigationOffset = 0;

    // Handle direction-specific navigation
    if (isHorizontal) {
      if (this.matchesKey(event, this.keys.left)) {
        event.preventDefault();
        shouldNavigate = true;
        navigationOffset = -1;
      } else if (this.matchesKey(event, this.keys.right)) {
        event.preventDefault();
        shouldNavigate = true;
        navigationOffset = 1;
      }
    }

    if (shouldNavigate) {
      this.navigateByOffset(navigationOffset);
      return;
    }

    if (this.matchesKey(event, this.keys.up)) {
      event.preventDefault();
      shouldNavigate = true;
      navigationOffset = -1;
    } else if (this.matchesKey(event, this.keys.down)) {
      event.preventDefault();
      shouldNavigate = true;
      navigationOffset = 1;
    }

    if (shouldNavigate) {
      this.navigateByOffset(navigationOffset);
      return;
    }
  }

  /**
   * Handle keyup event
   */
  private handleKeyUpEvent(_event: KeyboardEvent): void {
    // No special handling needed for key up events in simple navigation
  }

  /**
   * Navigate to a specific index
   */
  navigateToIndex(newIndex: number): boolean {
    if (!this.enabled) {
      return false;
    }

    if (newIndex >= 0 && newIndex < this.itemCount) {
      this.currentIndex = newIndex;
      this.onIndexChange?.(newIndex);
      this.notifyChange();
      return true;
    }
    return false;
  }

  /**
   * Navigate by offset
   */
  navigateByOffset(offset: number): boolean {
    if (!this.enabled) {
      return false;
    }

    const newIndex = this.currentIndex + offset;

    if (newIndex >= 0 && newIndex < this.itemCount) {
      return this.navigateToIndex(newIndex);
    }
    return false;
  }

  /**
   * Subscribe to changes
   */
  subscribe(callback: KeyboardNavigationChangeCallback): () => void {
    this.changeCallbacks.add(callback);
    return () => {
      this.changeCallbacks.delete(callback);
    };
  }

  /**
   * Notify all subscribers of changes
   */
  private notifyChange(): void {
    this.changeCallbacks.forEach((callback) => {
      callback(this.currentIndex);
    });
  }

  /**
   * Update item count
   */
  updateItemCount(itemCount: number): void {
    const changed = this.itemCount !== itemCount;
    this.itemCount = itemCount;
    // Clamp current index if needed
    if (this.currentIndex >= itemCount) {
      this.currentIndex = Math.max(0, itemCount - 1);
      this.notifyChange();
    } else if (changed) {
      this.notifyChange();
    }
  }

  /**
   * Update target element
   */
  updateTargetElement(element: HTMLElement | Window | null): void {
    if (this.targetElement !== element) {
      this.detachListeners();
      this.targetElement = element;
      this.attachListeners();
    }
  }

  /**
   * Update enabled state
   */
  updateEnabled(enabled: boolean): void {
    if (this.enabled !== enabled) {
      this.enabled = enabled;
      if (this.targetElement === window) {
        const globalHandler = GlobalKeyboardHandler.getInstance();
        if (enabled) {
          if (this.handleKeyDown)
            globalHandler.registerKeyDown(this.handleKeyDown);
          if (this.handleKeyUp) globalHandler.registerKeyUp(this.handleKeyUp);
        } else {
          if (this.handleKeyDown)
            globalHandler.unregisterKeyDown(this.handleKeyDown);
          if (this.handleKeyUp) globalHandler.unregisterKeyUp(this.handleKeyUp);
        }
      } else {
        if (enabled) {
          this.attachListeners();
        } else {
          this.detachListeners();
        }
      }
    }
  }

  /**
   * Update keys configuration
   */
  updateKeys(keys: Partial<KeyboardNavigationKeys>): void {
    this.keys = { ...this.keys, ...keys };
  }

  /**
   * Update the onIndexChange callback
   */
  public updateOnIndexChange(
    newCallback: ((index: number) => void) | undefined
  ): void {
    this.onIndexChange = newCallback;
  }

  /**
   * Get current index
   */
  getCurrentIndex(): number {
    return this.currentIndex;
  }

  /**
   * Destroy the manager and clean up
   */
  destroy(): void {
    if (this.targetElement === window) {
      const globalHandler = GlobalKeyboardHandler.getInstance();
      if (this.handleKeyDown)
        globalHandler.unregisterKeyDown(this.handleKeyDown);
      if (this.handleKeyUp) globalHandler.unregisterKeyUp(this.handleKeyUp);
    } else {
      this.detachListeners();
    }
    this.changeCallbacks.clear();
    this.handleKeyDown = null;
    this.handleKeyUp = null;
  }
}
